import React, { useState } from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

// Modern Service Card Component
const ServiceCard: React.FC<{
  service: {
    title: string;
    subtitle: string;
    description: string;
    icon: string;
    gradient: string;
    features: string[];
  };
  index: number;
}> = ({ service, index }) => {
  const [cardRef, cardVisible] = useScrollAnimation(0.2);
  const [, setIsHovered] = useState(false);

  return (
    <div 
      ref={cardRef}
      className={`group relative transition-all duration-700 ${
        cardVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-10'
      }`}
      style={{ 
        animationDelay: `${index * 150}ms`,
        transitionDelay: `${index * 150}ms`
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background Glow */}
      <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} rounded-3xl blur-2xl opacity-0 group-hover:opacity-30 transition-all duration-700 -z-10 scale-110`}></div>
      
      {/* Main Card */}
      <div className="relative bg-white/90 backdrop-blur-xl border border-white/40 rounded-3xl p-8 transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-brand-orange/25 group-hover:scale-105 group-hover:-translate-y-3 overflow-hidden">
        
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity duration-500">
          <div className="absolute top-4 right-4 w-20 h-20 border border-brand-orange/30 rounded-full animate-spin" style={{ animationDuration: '20s' }}></div>
          <div className="absolute bottom-4 left-4 w-16 h-16 border border-brand-gold/30 rounded-full animate-spin" style={{ animationDuration: '15s', animationDirection: 'reverse' }}></div>
        </div>

        {/* Header */}
        <div className="relative mb-6">
          <div className="flex items-center space-x-4 mb-4">
            <div className={`w-16 h-16 bg-gradient-to-br ${service.gradient} rounded-2xl flex items-center justify-center text-2xl shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
              {service.icon}
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 group-hover:text-brand-orange transition-colors duration-300">
                {service.title}
              </h3>
              <p className="text-sm font-medium bg-gradient-to-r from-brand-orange to-brand-gold bg-clip-text text-transparent">
                {service.subtitle}
              </p>
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-600 mb-6 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
          {service.description}
        </p>

        {/* Features List */}
        <div className="space-y-3">
          {service.features.map((feature, featureIndex) => (
            <div key={featureIndex} className="flex items-center space-x-3 group-hover:translate-x-2 transition-transform duration-300" style={{ transitionDelay: `${featureIndex * 50}ms` }}>
              <div className="w-2 h-2 bg-gradient-to-r from-brand-orange to-brand-gold rounded-full flex-shrink-0"></div>
              <span className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors duration-300">{feature}</span>
            </div>
          ))}
        </div>

        {/* Hover Effect Overlay */}
        <div className={`absolute inset-0 bg-gradient-to-br from-brand-orange/5 to-brand-gold/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`}></div>
        
        {/* Corner Accents */}
        <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-brand-orange/30 rounded-tr-2xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
        <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-brand-gold/30 rounded-bl-2xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
      </div>
    </div>
  );
};

const Services: React.FC = () => {
  const [headerRef, headerVisible] = useScrollAnimation(0.1);
  const [ctaRef, ctaVisible] = useScrollAnimation(0.3);

  const services = [
    {
      title: "ATL Advertising",
      subtitle: "Above The Line",
      description: "Premium above-the-line advertising campaigns that build brand awareness and drive customer acquisition through traditional media channels.",
      icon: "📺",
      gradient: "from-blue-500 to-blue-600",
      features: [
        "Television Commercials",
        "Radio Advertising",
        "Print Media Campaigns",
        "Outdoor Advertising"
      ]
    },
    {
      title: "BTL Activations",
      subtitle: "Below The Line",
      description: "Engaging below-the-line activations that create memorable brand experiences and drive direct customer engagement.",
      icon: "🎪",
      gradient: "from-brand-orange to-brand-gold",
      features: [
        "Event Marketing",
        "Trade Show Exhibitions",
        "Product Launches",
        "Customer Engagement Programs"
      ]
    },
    {
      title: "3D Billboards & Displays",
      subtitle: "Immersive Experiences",
      description: "Revolutionary 3D billboard and display solutions that create immersive brand experiences and capture audience attention.",
      icon: "🏗️",
      gradient: "from-brand-primary to-brand-orange",
      features: [
        "3D Billboard Design",
        "Interactive Displays",
        "Augmented Reality Experiences",
        "Immersive Installations"
      ]
    },
    {
      title: "PR Campaigns & Influencer Marketing",
      subtitle: "Strategic Communication",
      description: "Comprehensive public relations campaigns and influencer partnerships that build brand credibility and reach your target audience through trusted voices.",
      icon: "📢",
      gradient: "from-brand-orange to-brand-gold",
      features: [
        "Media Relations & Press Releases",
        "Influencer Partnership Programs",
        "Crisis Communication Management",
        "Brand Reputation Building"
      ]
    },
    {
      title: "Digital Campaigns & Software Development",
      subtitle: "Digital Innovation",
      description: "Cutting-edge digital marketing campaigns and custom software solutions that drive engagement and deliver measurable results for your aviation brand.",
      icon: "💻",
      gradient: "from-brand-primary to-brand-secondary",
      features: [
        "Social Media Campaigns",
        "Custom Web Applications",
        "Digital Marketing Automation",
        "Performance Analytics & Reporting"
      ]
    },
    {
      title: "2D & 3D Site Maps & Event Layout Rendering",
      subtitle: "Visual Planning Excellence",
      description: "Professional 2D and 3D site mapping and event layout rendering services that bring your vision to life with stunning visual representations.",
      icon: "🗺️",
      gradient: "from-brand-secondary to-brand-accent",
      features: [
        "2D Site Mapping & Blueprints",
        "3D Event Layout Rendering",
        "Interactive Floor Plans",
        "Virtual Event Planning Tools"
      ]
    }
  ];

  return (
    <section id="services" className="relative py-16 sm:py-20 lg:py-24 overflow-hidden">
      {/* Dynamic Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        {/* Animated Grid */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(249, 115, 22, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(249, 115, 22, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            animation: 'gridMove 20s linear infinite'
          }}></div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-brand-orange/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-brand-gold/15 rounded-full blur-2xl animate-bounce" style={{ animationDuration: '3s' }}></div>
        <div className="absolute bottom-20 left-1/4 w-20 h-20 bg-brand-primary/10 rounded-full blur-xl animate-ping" style={{ animationDuration: '4s' }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-16 sm:mb-20 transition-all duration-1000 ${
            headerVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-brand-orange/10 to-brand-gold/10 border border-brand-orange/20 rounded-full px-6 py-3 mb-6">
            <div className="w-2 h-2 bg-brand-orange rounded-full animate-pulse"></div>
            <span className="text-sm font-semibold bg-gradient-to-r from-brand-orange to-brand-gold bg-clip-text text-transparent">
              Complete Brand Solutions
            </span>
          </div>

          {/* Title */}
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-gray-900 via-brand-primary to-gray-900 bg-clip-text text-transparent">
            Our Services
            </span>
          </h2>

          {/* Subtitle */}
          <p className="text-xl sm:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Comprehensive branding and advertisement solutions to make your brand stand out from the crowd.
          </p>

          {/* Gradient Divider */}
          <div className="flex justify-center mt-8">
            <div className="w-24 h-1 bg-gradient-to-r from-brand-orange to-brand-gold rounded-full"></div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 mb-16">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>

        {/* CTA Section */}
        <div 
          ref={ctaRef}
          className={`text-center transition-all duration-1000 ${
            ctaVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="relative bg-gradient-to-br from-gray-900 via-brand-primary to-gray-900 rounded-3xl p-8 sm:p-12 text-white overflow-hidden">
            {/* Background Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-orange/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-brand-gold/10 rounded-full blur-2xl"></div>
            
            <div className="relative z-10">
              <h3 className="text-3xl sm:text-4xl font-bold mb-6">
                Ready to Transform Your Brand?
              </h3>
              <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Let's discuss your project and create a customized strategy that drives results for your aviation brand.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => window.location.href = '/quote'}
                  className="group relative bg-gradient-to-r from-brand-orange to-brand-gold text-white px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-xl hover:shadow-brand-orange/25 transition-all duration-300 hover:scale-105 overflow-hidden"
                >
                  <span className="relative z-10 flex items-center justify-center space-x-2">
                    <span>Get Started Today</span>
                    <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                  <div className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white/20 to-transparent transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                </button>
                
                <button 
                  onClick={() => {
                    const portfolioSection = document.getElementById('portfolio');
                    if (portfolioSection) {
                      const headerHeight = 120;
                      const elementPosition = portfolioSection.offsetTop - headerHeight;
                      window.scrollTo({
                        top: elementPosition,
                        behavior: 'smooth'
                      });
                    }
                  }}
                  className="group bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white/20 transition-all duration-300 hover:scale-105"
                >
                  <span className="flex items-center justify-center space-x-2">
                    <span>View Our Work</span>
                    <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </span>
            </button>
          </div>
        </div>
      </div>
        </div>
      </div>

      <style>{`
        @keyframes gridMove {
          0% { transform: translate(0, 0); }
          100% { transform: translate(50px, 50px); }
        }
      `}</style>
    </section>
  );
};

export default Services;